import type {
  FormAnswerInput,
  FormQuestion,
  FormQuestionInput,
  FormSection,
  FormSectionInput,
  StoredFormAnswer,
} from './form-question';

/**
 * Feedback on a booked space, asked by an emailed private link once the booking ends — see
 * `backend/docs/booking-feedback.md`. A client is asked once per space. Each space asks its own
 * form if it has one, or else its kind's default: the desk form for the coworking floors, the
 * space form for every room and facility.
 */
export const BOOKING_FEEDBACK_FORM_KINDS = ['desk', 'space'] as const;
export type BookingFeedbackFormKind = (typeof BOOKING_FEEDBACK_FORM_KINDS)[number];

/** The two fields every booking feedback form asks, fixed by the API. */
export interface BookingFeedbackStandardFields {
  overallRating: { required: true; min: 1; max: 5 };
  comment: { required: false; maxLength: 5000 };
}

export interface BookingFeedbackForm {
  kind: BookingFeedbackFormKind;
  /** Null for a kind's default form; the space it belongs to otherwise. */
  resourceId: string | null;
  version: number;
  standardFields: BookingFeedbackStandardFields;
  ungroupedQuestions: FormQuestion[];
  sections: FormSection[];
  updatedAt: string | null;
}

export interface ReplaceBookingFeedbackFormRequest {
  /** 0 to create a space's own form. */
  expectedVersion: number;
  ungroupedQuestions?: FormQuestionInput[];
  sections?: FormSectionInput[];
}

/** A space as the feedback settings list it: its switch, and which form it asks. */
export interface BookingFeedbackSpace {
  id: string;
  name: string;
  kind: BookingFeedbackFormKind;
  isActive: boolean;
  isEnabled: boolean;
  /** When it was last switched on. Bookings that ended before it are never asked about. */
  enabledAt: string | null;
  /** True when it asks its own form rather than its kind's default. */
  hasOwnForm: boolean;
}

export interface UpdateBookingFeedbackSpaceRequest {
  isEnabled: boolean;
}

/** What the emailed link opens: the booking it asks about, never the client's contact details. */
export interface GuestBookingFeedbackForm {
  booking: {
    reference: string;
    kind: BookingFeedbackFormKind;
    spaceName: string;
    /** The rate-card item booked: "Monthly Desk", "Small Meeting Room". */
    itemName: string;
    bookingDate: string;
    /** Inclusive; the same as `bookingDate` for a one-day booking. */
    bookingEndDate: string;
    /** Minutes after midnight, or null for a full-day desk. */
    startMinute: number | null;
    endMinute: number | null;
  };
  alreadySubmitted: boolean;
  standardFields: BookingFeedbackStandardFields;
  ungroupedQuestions: FormQuestion[];
  sections: FormSection[];
}

export interface SubmitBookingFeedbackRequest {
  overallRating: number;
  comment?: string;
  answers?: FormAnswerInput[];
}

export interface SubmitBookingFeedbackResponse {
  id: string;
  submittedAt: string;
}

/** One response, with who gave it: booking feedback is not anonymous. */
export interface AdminBookingFeedbackResponse {
  id: string;
  kind: BookingFeedbackFormKind;
  overallRating: number;
  comment: string;
  answers: StoredFormAnswer[];
  submittedAt: string;
  booking: {
    id: string;
    reference: string;
    itemName: string;
    bookingDate: string;
    bookingEndDate: string;
  };
  space: { id: string; name: string };
  customer: { id: string; name: string; email: string; phone: string | null };
}

/** Over the kind and space filters; the rating filter narrows the rows only. */
export interface AdminBookingFeedbackSummary {
  invitations: number;
  totalResponses: number;
  responseRatePercent: number;
  averageOverallRating: number | null;
  ratingDistribution: Record<string, number>;
}

export interface AdminBookingFeedbackResponseList {
  data: AdminBookingFeedbackResponse[];
  summary: AdminBookingFeedbackSummary;
  /** Every space a client has been asked about, whatever the filters: the space picker's list. */
  spaces: { id: string; name: string; kind: BookingFeedbackFormKind }[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}
